/**
 * PokemonCard - Lavender UI redesign
 * 3-column layout: [Sprite + Meta] [Name + Moves] [Stats]
 * Matches Google Studio reference image
 */
import { useState } from 'react';
import { X, Edit3 } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { formatPokemonName, formatAbilityName, formatMoveName, formatItemName } from '../../data/pokemonTranslations';
import styles from '../../pages/DexBuilder.module.css';

const TYPE_COLORS = {
  normal: '#A8A878', fire: '#F08030', water: '#6890F0', electric: '#F8D030',
  grass: '#78C850', ice: '#98D8D8', fighting: '#C03028', poison: '#A040A0',
  ground: '#E0C068', flying: '#A890F0', psychic: '#F85888', bug: '#A8B820',
  rock: '#B8A038', ghost: '#705898', dragon: '#7038F8', dark: '#705848',
  steel: '#B8B8D0', fairy: '#EE99AC', stellar: '#40B5A5'
};

const TYPE_NAMES = {
  normal: { en: 'Normal', es: 'Normal' },
  fire: { en: 'Fire', es: 'Fuego' },
  water: { en: 'Water', es: 'Agua' },
  electric: { en: 'Electric', es: 'Eléctrico' },
  grass: { en: 'Grass', es: 'Planta' },
  ice: { en: 'Ice', es: 'Hielo' },
  fighting: { en: 'Fighting', es: 'Lucha' },
  poison: { en: 'Poison', es: 'Veneno' },
  ground: { en: 'Ground', es: 'Tierra' },
  flying: { en: 'Flying', es: 'Volador' },
  psychic: { en: 'Psychic', es: 'Psíquico' },
  bug: { en: 'Bug', es: 'Bicho' },
  rock: { en: 'Rock', es: 'Roca' },
  ghost: { en: 'Ghost', es: 'Fantasma' },
  dragon: { en: 'Dragon', es: 'Dragón' },
  dark: { en: 'Dark', es: 'Siniestro' },
  steel: { en: 'Steel', es: 'Acero' },
  fairy: { en: 'Fairy', es: 'Hada' },
  stellar: { en: 'Stellar', es: 'Estelar' }
};

export function PokemonCard({ slot, index, onEdit, onRemove }) {
  const { language } = useApp();
  const [isHovered, setIsHovered] = useState(false);

  // Empty slot
  if (!slot?.data) {
    return (
      <div
        className={styles.card}
        onClick={() => onEdit(index)}
        data-testid={`empty-slot-${index}`}
      >
        <div className={styles.emptySlot}>
          <span className={styles.emptySlotIcon}>+</span>
          <span className={styles.emptySlotText}>
            {language === 'es' ? 'Añadir Pokémon' : 'Add Pokémon'}
          </span>
        </div>
      </div>
    );
  }

  const pokemon = slot.data;
  const types = pokemon.types?.map(t => t.type.name) || [];
  const nickname = slot.nickname || formatPokemonName(pokemon.name, language);

  // Item info
  const itemName = typeof slot.item === 'object' ? slot.item?.name : slot.item;
  const itemSprite = typeof slot.item === 'object' ? slot.item?.sprite : (slot.item ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/${slot.item}.png` : '');

  // Base stats
  const getBaseStat = (statName) => {
    const s = pokemon.stats?.find(st => st.stat.name === statName);
    return s?.base_stat || 0;
  };

  const statList = [
    { key: 'hp', label: 'HP', value: getBaseStat('hp') },
    { key: 'atk', label: 'ATK', value: getBaseStat('attack') },
    { key: 'def', label: 'DEF', value: getBaseStat('defense') },
    { key: 'spa', label: 'SPA', value: getBaseStat('special-attack') },
    { key: 'spd', label: 'SPD', value: getBaseStat('special-defense') },
    { key: 'spe', label: 'SPE', value: getBaseStat('speed') },
  ];

  // Sprite URL — use official artwork (larger, cleaner) like the reference
  const spriteUrl = pokemon.sprites?.other?.['official-artwork']?.front_default
    || pokemon.sprites?.front_default;
  const shinySpriteUrl = pokemon.sprites?.other?.['official-artwork']?.front_shiny
    || pokemon.sprites?.front_shiny
    || spriteUrl;
  const miniSpriteUrl = pokemon.sprites?.front_default;

  return (
    <div
      className={styles.card}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onEdit(index)}
      data-testid={`team-slot-${index}`}
    >
      {/* Remove Button */}
      {isHovered && (
        <button
          className={styles.removeButton}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(index);
          }}
          aria-label="Remove"
        >
          <X size={14} />
        </button>
      )}

      {/* 3-column layout */}
      <div className={styles.cardInner}>
        {/* === Column 1: Sprite + Meta === */}
        <div className={styles.colLeft}>
          <div className={styles.spriteWrap}>
            <img
              src={slot.shiny ? shinySpriteUrl : spriteUrl}
              alt={pokemon.name}
              className={styles.sprite}
              loading="lazy"
              crossOrigin="anonymous"
            />
            {slot.shiny && <div className={styles.shinyIndicator}>✨</div>}
            {miniSpriteUrl && (
              <img
                src={miniSpriteUrl}
                alt=""
                className={styles.miniSprite}
                loading="lazy"
              />
            )}
          </div>

          <div className={styles.metaSection}>
            <div className={styles.metaLabel}>
              {language === 'es' ? 'Habilidad' : 'Ability'}
            </div>
            <div className={styles.metaValue}>
              {formatAbilityName(slot.ability || pokemon.abilities?.[0]?.ability.name, language)}
            </div>

            <div className={styles.metaLabel}>
              {language === 'es' ? 'Naturaleza' : 'Nature'}
            </div>
            <div className={styles.metaValuePurple}>
              {slot.nature ? slot.nature.charAt(0).toUpperCase() + slot.nature.slice(1) : '—'}
            </div>

            <div className={styles.metaLabel}>
              {language === 'es' ? 'Objeto' : 'Held Item'}
            </div>
            <div className={styles.itemRow}>
              {itemSprite ? (
                <img
                  src={itemSprite}
                  alt={itemName}
                  className={styles.itemIcon}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className={styles.itemIconPlaceholder}>
                  <div className={styles.itemIconDot} />
                </div>
              )}
              <span className={styles.metaValue} style={{ fontSize: '0.7rem' }}>
                {itemName ? formatItemName(itemName, language) : '—'}
              </span>
            </div>
          </div>
        </div>

        {/* === Right Area: Name+Types / Moves+Stats === */}
        <div className={styles.colContent}>
          {/* Name + Types inline */}
          <div className={styles.nameRow}>
            <div className={styles.pokemonName}>{nickname}</div>
            <div className={styles.typeBadges}>
              {types.map(type => (
                <span
                  key={type}
                  className={styles.typeBadge}
                  style={{
                    backgroundColor: TYPE_COLORS[type],
                    color: ['electric', 'ice', 'flying', 'fairy', 'steel', 'normal'].includes(type)
                      ? '#333' : '#fff'
                  }}
                >
                  {TYPE_NAMES[type]?.[language] || type}
                </span>
              ))}
            </div>
          </div>

          {/* Moves + Stats side by side */}
          <div className={styles.bodyRow}>
            <div className={styles.movesSection}>
              {(slot.moves || ['', '', '', '']).slice(0, 4).map((move, i) => (
                <div key={i} className={styles.moveSlot}>
                  {move ? (
                    <>
                      <div
                        className={styles.moveDot}
                        style={{ backgroundColor: TYPE_COLORS[getMoveType(move, pokemon)] || '#A8A878' }}
                      />
                      <span className={styles.moveName}>
                        {formatMoveName(move, language)}
                      </span>
                    </>
                  ) : (
                    <span className={styles.moveEmpty}>—</span>
                  )}
                </div>
              ))}
            </div>

            <div className={styles.statsSection}>
              {statList.map(stat => (
                <div key={stat.key} className={styles.statRow}>
                  <span className={styles.statLabel}>{stat.label}</span>
                  <span className={styles.statValue}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Button */}
      {isHovered && (
        <button
          className={styles.editButton}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(index);
          }}
          aria-label="Edit"
        >
          <Edit3 size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * Attempt to get the type of a move from the pokemon's move data.
 * Falls back to 'normal' if not found (type data requires extra API call).
 */
function getMoveType(moveName, pokemonData) {
  // PokeAPI pokemon endpoint doesn't include move types directly,
  // but we can try to infer from the pokemon's types for display
  // This is a visual hint only — the dot color
  if (!moveName || !pokemonData) return 'normal';

  // If the move matches a type name, use that color
  const moveSlug = moveName.toLowerCase().replace(/\s+/g, '-');
  const typeKeywords = {
    'flamethrower': 'fire', 'fire-blast': 'fire', 'heat-wave': 'fire', 'flare-blitz': 'fire',
    'ember': 'fire', 'fire-punch': 'fire', 'flame-charge': 'fire', 'fire-spin': 'fire',
    'hydro-pump': 'water', 'surf': 'water', 'scald': 'water', 'waterfall': 'water',
    'aqua-jet': 'water', 'water-pulse': 'water', 'muddy-water': 'water', 'liquidation': 'water',
    'thunderbolt': 'electric', 'thunder': 'electric', 'volt-switch': 'electric', 'wild-charge': 'electric',
    'discharge': 'electric', 'thunder-wave': 'electric', 'electro-ball': 'electric',
    'solar-beam': 'grass', 'leaf-storm': 'grass', 'energy-ball': 'grass', 'giga-drain': 'grass',
    'seed-bomb': 'grass', 'leaf-blade': 'grass', 'wood-hammer': 'grass', 'grass-knot': 'grass',
    'sleep-powder': 'grass', 'spore': 'grass',
    'ice-beam': 'ice', 'blizzard': 'ice', 'ice-punch': 'ice', 'icy-wind': 'ice',
    'freeze-dry': 'ice', 'ice-shard': 'ice', 'aurora-veil': 'ice',
    'earthquake': 'ground', 'earth-power': 'ground', 'dig': 'ground', 'bulldoze': 'ground',
    'high-horsepower': 'ground', 'stomping-tantrum': 'ground',
    'psychic': 'psychic', 'psyshock': 'psychic', 'zen-headbutt': 'psychic', 'future-sight': 'psychic',
    'expanding-force': 'psychic', 'trick': 'psychic', 'calm-mind': 'psychic',
    'close-combat': 'fighting', 'aura-sphere': 'fighting', 'focus-blast': 'fighting',
    'drain-punch': 'fighting', 'mach-punch': 'fighting', 'brick-break': 'fighting',
    'superpower': 'fighting', 'sacred-sword': 'fighting',
    'sludge-bomb': 'poison', 'poison-jab': 'poison', 'toxic': 'poison', 'gunk-shot': 'poison',
    'sludge-wave': 'poison', 'venoshock': 'poison',
    'air-slash': 'flying', 'brave-bird': 'flying', 'hurricane': 'flying', 'acrobatics': 'flying',
    'fly': 'flying', 'aerial-ace': 'flying', 'tailwind': 'flying',
    'shadow-ball': 'ghost', 'shadow-claw': 'ghost', 'hex': 'ghost', 'phantom-force': 'ghost',
    'shadow-sneak': 'ghost', 'spectral-thief': 'ghost', 'poltergeist': 'ghost',
    'dragon-claw': 'dragon', 'draco-meteor': 'dragon', 'dragon-pulse': 'dragon',
    'outrage': 'dragon', 'dragon-dance': 'dragon', 'scale-shot': 'dragon',
    'dark-pulse': 'dark', 'crunch': 'dark', 'knock-off': 'dark', 'sucker-punch': 'dark',
    'throat-chop': 'dark', 'foul-play': 'dark', 'parting-shot': 'dark', 'nasty-plot': 'dark',
    'iron-head': 'steel', 'flash-cannon': 'steel', 'meteor-mash': 'steel', 'steel-beam': 'steel',
    'bullet-punch': 'steel', 'heavy-slam': 'steel',
    'moonblast': 'fairy', 'dazzling-gleam': 'fairy', 'play-rough': 'fairy',
    'draining-kiss': 'fairy', 'fairy-wind': 'fairy', 'spirit-break': 'fairy',
    'u-turn': 'bug', 'x-scissor': 'bug', 'bug-buzz': 'bug', 'megahorn': 'bug',
    'first-impression': 'bug', 'leech-life': 'bug',
    'stone-edge': 'rock', 'rock-slide': 'rock', 'stealth-rock': 'rock', 'power-gem': 'rock',
    'rock-blast': 'rock', 'head-smash': 'rock',
    'body-slam': 'normal', 'return': 'normal', 'hyper-voice': 'normal', 'extreme-speed': 'normal',
    'fake-out': 'normal', 'rapid-spin': 'normal', 'protect': 'normal', 'swords-dance': 'normal',
    'substitute': 'normal', 'recover': 'normal', 'rest': 'normal', 'wish': 'normal',
    'toxic-spikes': 'poison', 'spikes': 'ground', 'defog': 'flying', 'roost': 'flying',
    'will-o-wisp': 'fire', 'trick-room': 'psychic', 'teleport': 'psychic',
  };

  return typeKeywords[moveSlug] || 'normal';
}

export default PokemonCard;
